import { IProductDTO } from '../../../legacy/shared-types';
import { Alert, App, Card, Flex, Spin, Tooltip } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import { btlProductApi } from '../../../api/product';
import { FormValidationContext, useOnNext } from '../../../hooks';
import { useBtlApplicationInProgressById } from '../../../queries';
import { useBtlProductByAppId } from '../../../queries/products/useBtlProductByAppId';
import { TypedFormatMessageFn } from '../../../types/intl';
import { BtlFormSectionParam } from '../types';
import { useBtlFormMappedRoutes } from '../useBtlFormMappedRoutes';
import { monthsToYears } from '../../../utils/numbers';
import { parseBooleanish } from '../../../utils';

export const ProductTitle = ({
  maximumFTV,
  monthsInitial,
  productType,
}: {
  maximumFTV: number;
  monthsInitial: number;
  productType: string;
}) => {
  const ftv = (maximumFTV * 100).toFixed(0);
  const term = monthsToYears(monthsInitial);
  return (
    <>
      {term}yr {productType.toLowerCase()} {ftv}% FTV
    </>
  );
};

export const ProductItem = ({
  product,
  isSelected,
  onProductSelected,
  isSelectable,
  invalidated,
  ...props
}: {
  product: IProductDTO;
  isSelected?: boolean;
  onProductSelected?: (product: IProductDTO) => void;
  isSelectable: boolean;
  invalidated?: boolean;
} & React.HTMLAttributes<HTMLDivElement>) => {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const { metadata, maximumFTV, monthsInitial, productType } = product as IProductDTO & {
    metadata?: { btlIsExpat?: boolean | string; btlPropertyType?: string };
  };
  const { btlPropertyType = '' } = metadata ?? {};
  const isExpat = parseBooleanish(metadata?.btlIsExpat) ?? false;
  const propertyTypeLabel = btlPropertyType === 'STANDARD' ? 'standard' : 'hmoOrMub';

  const selectedCardClassName = invalidated
    ? 'border-2 border-yellow-500 bg-yellow-50/50'
    : 'border-2 border-[#008236] bg-[#DCFCE7]';
  const defaultCardClassName = invalidated
    ? 'border border-yellow-500'
    : 'border border-gray-200 hover:border-[#008236] cursor-pointer';

  const card = (
    <Card
      {...props}
      className={`transition ${isSelected ? selectedCardClassName : defaultCardClassName}
${!isSelectable && 'pointer-events-none'}
${props.className || ''}`}
      onClick={() => isSelectable && onProductSelected?.(product)}
    >
      <div className={'font-bold'}>
        <ProductTitle maximumFTV={maximumFTV} monthsInitial={monthsInitial} productType={productType} />
      </div>
      {!!btlPropertyType && (
        <div className={'capitalize'}>
          {formatMessage({
            id: `btlApplicationForm.PRODUCT.${propertyTypeLabel}.label`,
          })}{' '}
          property
        </div>
      )}
      <div className={'capitalize'}>
        {formatMessage({ id: `btlApplicationForm.PRODUCT.${isExpat ? 'isExpat' : 'isResident'}.label` })}
      </div>
      <div className={'flex gap-2 items-end'}>
        <div>{formatMessage({ id: 'btlApplicationForm.PRODUCT.rate.label' })}</div>
        <div className={'text-end grow font-bold text-lg'}>{(product.initialBaseRate * 100).toFixed(2)}%</div>
      </div>
      <div className={'flex gap-2 items-end'}>
        <div>{formatMessage({ id: 'btlApplicationForm.PRODUCT.productFee.label' })}</div>
        <span className={'text-end grow font-bold text-lg'}>{(product.productFeesPercentage * 100).toFixed(2)}%</span>
      </div>
    </Card>
  );

  return invalidated ? (
    <Tooltip
      defaultOpen
      style={{ visibility: invalidated ? 'visible' : 'hidden' }}
      title={formatMessage({ id: 'btlApplicationForm.PRODUCT.invalidated.label' })}
      placement={'bottom'}
    >
      {card}
    </Tooltip>
  ) : (
    card
  );
};

export const ProductSelector = () => {
  const { message } = App.useApp();
  const [selectedProduct, setSelectedProduct] = useState<IProductDTO>();
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const { id = '' } = useParams();
  const { data, isLoading } = useBtlApplicationInProgressById(id);
  const { data: btlProducts, isLoading: isProductsLoading } = useBtlProductByAppId(id);
  const navigate = useNavigate();
  const { nextSection, isFormEditable } = useBtlFormMappedRoutes(data);

  const updateSectionInUrl = (section: BtlFormSectionParam) => {
    navigate({
      search: `?section=${section}`,
    });
  };

  const formValidationContext = useContext(FormValidationContext);
  if (!formValidationContext) throw new Error('ProductSelector must be used within a FormValidationProvider');
  const { setIsValid, setIsSubmitting } = formValidationContext;

  useEffect(() => {
    const isFormValid = !!selectedProduct;
    setIsValid(!!isFormValid);
  }, [selectedProduct]);

  useEffect(() => {
    setSelectedProduct(data?.product);
  }, [data]);

  useOnNext(async () => {
    if (!selectedProduct) return;
    setIsSubmitting(true);
    try {
      await btlProductApi.updateBtlProduct(id, selectedProduct.id);
      if (nextSection) {
        updateSectionInUrl(nextSection);
      }
    } catch (error: any) {
      console.error(error);
      message.error(error.message);
    } finally {
      setIsSubmitting(false);
    }
  });

  if (isLoading || isProductsLoading) return <Spin size={'large'}></Spin>;

  const activeBtlProducts = btlProducts?.filter(product => product.isActive);
  const sortedProducts = activeBtlProducts?.sort(
    (productA, productB) =>
      productA.monthsInitial - productB.monthsInitial || productA.maximumFTV - productB.maximumFTV,
  );

  return (
    <Flex gap={16} wrap>
      {sortedProducts?.length ? (
        (selectedProduct && !data?.isCurrentProductSuitable && selectedProduct?.id === data?.product?.id
          ? [
              <ProductItem
                key={'current-product'}
                product={selectedProduct}
                isSelected={true}
                isSelectable={true}
                style={{ opacity: 0.5 }}
                invalidated
              />,
            ]
          : []
        ).concat(
          sortedProducts?.map((product, index) => {
            return (
              <ProductItem
                key={index}
                product={product}
                isSelected={selectedProduct?.id === product.id}
                onProductSelected={product => setSelectedProduct(product)}
                isSelectable={!!isFormEditable}
              />
            );
          }),
        )
      ) : (
        <Alert
          description={formatMessage({ id: 'btlApplicationForm.PRODUCT.noProducts.message' })}
          message={formatMessage({ id: 'btlApplicationForm.PRODUCT.noProducts.title' })}
          type='warning'
          showIcon
        />
      )}
    </Flex>
  );
};

export default ProductSelector;
